import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCompany, addDomain, verifyDomain, upsertService, getDomains } from '../../lib/api';
import { CheckCircle, Copy, Check } from 'lucide-react';

const MonitoringOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [tier, setTier] = useState('');
  const [domain, setDomain] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('');
  const [domainId, setDomainId] = useState(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);
  const [copied, setCopied] = useState(false);
  // fromLimit=true means user hit the free-tier domain cap
  const [fromLimit, setFromLimit] = useState(false);

  // If returning from company setup, the tier is in the URL
  // If coming from free-tier limit, fromLimit=true is in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tierParam = params.get('tier');
    const fromLimitParam = params.get('fromLimit');
    if (tierParam) {
      setTier(tierParam);
      setStep(2);
    }
    if (fromLimitParam === 'true') {
      setFromLimit(true);
      // Stay on step 1 (plan chooser) so user sees upgrade options
      setStep(1);
    }
  }, []);

  // ── Step 1: pick a tier ──
  const handleTierSelect = (selectedTier) => {
    setTier(selectedTier);
    setStep(2);
  };

  // ── Step 2: add domain ──
  const handleDomainSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!verificationMethod) {
      setError('Please select a verification method');
      return;
    }

    let cleaned = domain.trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '');

    if (!/^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(cleaned)) {
      setError('Please enter a valid domain (e.g., example.com)');
      return;
    }

    setDomain(cleaned);
    setLoading(true);

    try {
      // Check if the user has a company
      const { company } = await getCompany();
      if (!company) {
        navigate(`/dashboard/company-setup?returnTo=${encodeURIComponent('/dashboard/monitoring/onboarding?tier=' + tier)}`);
        return;
      }

      // Check for duplicate domain before adding
      const { domains: existingDomains } = await getDomains();
      const duplicate = (existingDomains || []).find(
        d => d.domain_name.toLowerCase() === cleaned
      );
      if (duplicate) {
        setError(`Domain "${cleaned}" is already being monitored. Please enter a different domain.`);
        setLoading(false);
        return;
      }

      // Add the domain via the API (worker generates token + inserts into DB)
      const { domain: newDomain } = await addDomain({
        domain_name: cleaned,
        verification_method: verificationMethod.toUpperCase()
      });

      setDomainId(newDomain.id);
      setVerificationToken(newDomain.verification_token);
      setStep(3);
    } catch (err) {
      console.error('Domain add error:', err);
      setError('Failed to add domain: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: verify domain via real DNS/HTML check ──
  const handleVerify = async () => {
    setLoading(true);
    setError('');

    try {
      // Call the worker API — it does the DNS/HTML check AND updates the DB
      const result = await verifyDomain({
        domain,
        token: verificationToken,
        method: verificationMethod.toUpperCase()
      });

      if (!result.verified) {
        setError(result.error || 'Verification failed. Check your DNS/file setup and try again.');
        return;
      }

      setVerified(true);
      setTimeout(() => setStep(4), 1000);
    } catch (err) {
      console.error('Verify error:', err);
      setError('Verification check failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 4: complete — create service subscription ──
  const handleComplete = async () => {
    setLoading(true);
    try {
      await upsertService({
        service_type: 'TRUST_MONITORING',
        tier,
        onboarding_status: 'COMPLETED'
      });
    } catch (err) {
      console.error('Error saving service:', err);
    } finally {
      setLoading(false);
      navigate('/dashboard/monitoring');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Render ──
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '4rem 1rem' }}>
      {/* Progress Bar */}
      <div style={{ marginBottom: '3rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          {[1, 2, 3, 4].map((s) => (
            <div key={s} style={{
              flex: 1, height: '4px',
              background: s <= step ? 'var(--gold)' : 'var(--gray)',
              marginRight: s < 4 ? '0.5rem' : 0,
              borderRadius: '2px', transition: 'background 0.3s ease',
            }} />
          ))}
        </div>
        <p style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>Step {step} of 4</p>
      </div>

      {/* ── Step 1: Choose Plan ── */}
      {step === 1 && (
        <div>
          <h1 className="hero-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Choose Your Plan</h1>
          {fromLimit ? (
            <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid var(--gold)', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '2rem', fontSize: '0.875rem', color: 'var(--charcoal)' }}>
              <strong>Free tier limit reached.</strong> Your free plan supports 1 domain. Upgrade to Standard to monitor up to 5 domains.
            </div>
          ) : (
            <p style={{ color: 'var(--gray)', marginBottom: '2rem' }}>Select the monitoring tier that fits your needs</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              {
                name: 'starter', displayName: 'FREE', price: 'Free',
                positioning: 'For teams who want to understand their external posture.',
                features: ['1 domain', 'SSL/TLS monitoring', 'Email authentication checks', 'Basic blacklist monitoring', 'Basic uptime checks', 'Email alerts', '7-day history', 'Trust grade (A-F)'],
                tagline: 'You see the surface.',
              },
              {
                name: 'standard', displayName: 'STANDARD', price: '$199 / month',
                positioning: 'Operational Protection',
                subtitle: 'For teams that need defensible answers.',
                features: ['Everything in Free, plus:', 'Up to 5 domains', 'Credential breach monitoring', 'Certificate transparency alerts', 'CDN Security Check', 'Slack / Teams alerts', '90-day history', 'Detailed risk scoring', 'Exportable reports', 'Public trust page', 'Multi-user access'],
                tagline: 'You eliminate blind spots.', recommended: true,
              },
            ].map((plan) => (
              <div key={plan.name} className={plan.recommended ? 'card-emphasis' : 'card'}
                style={{ position: 'relative', background: plan.recommended ? 'rgba(245, 158, 11, 0.05)' : 'var(--white)', opacity: fromLimit && plan.name === 'starter' ? 0.55 : 1 }}>
                {plan.recommended && (
                  <div style={{ position: 'absolute', top: '-12px', right: '20px', background: 'var(--gold)', color: 'var(--black)', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>RECOMMENDED</div>
                )}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: plan.recommended ? 'var(--gold)' : 'var(--success)', marginBottom: '0.5rem', letterSpacing: '0.5px' }}>{plan.displayName}</div>
                  <h3 className="subsection-title" style={{ marginBottom: '0.25rem', fontSize: '1.1rem' }}>{plan.positioning}</h3>
                  {plan.subtitle && <p style={{ fontSize: '0.875rem', color: 'var(--charcoal)', fontStyle: 'italic', marginBottom: '1rem' }}>{plan.subtitle}</p>}
                  <div className="label" style={{ marginBottom: '0.5rem', marginTop: '1rem' }}>Includes:</div>
                  <ul style={{ fontSize: '0.875rem', color: 'var(--charcoal)', paddingLeft: '1.25rem', marginBottom: '1rem' }}>
                    {plan.features.map((f, i) => <li key={i} style={{ marginBottom: '0.25rem' }}>{f}</li>)}
                  </ul>
                  <p style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--charcoal)', marginBottom: '1rem' }}>{plan.tagline}</p>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>{plan.price}</div>
                  {plan.name === 'starter' ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ width: '100%', opacity: fromLimit ? 0.45 : 1, cursor: fromLimit ? 'not-allowed' : 'pointer' }}
                      disabled={fromLimit}
                      title={fromLimit ? 'Free tier is limited to 1 domain' : undefined}
                      onClick={() => !fromLimit && handleTierSelect(plan.name)}
                    >
                      Start Free
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      onClick={() => fromLimit ? navigate('/contact') : handleTierSelect(plan.name)}
                    >
                      {fromLimit ? 'Contact Support' : 'Upgrade to Standard'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Step 2: Add Domain ── */}
      {step === 2 && (
        <div>
          <h1 className="hero-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Add Your Domain</h1>
          <p style={{ color: 'var(--gray)', marginBottom: '2rem' }}>Enter the domain you want to monitor</p>

          <form onSubmit={handleDomainSubmit}>
            <div className="card-emphasis">
              {error && (
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>{error}</div>
              )}
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">Domain Name</label>
                <input type="text" value={domain} onChange={(e) => { setDomain(e.target.value); setError(''); }}
                  placeholder="example.com" required className="form-input" />
                <p style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: '0.5rem' }}>Enter without http:// or https://</p>
              </div>
              <div style={{ marginBottom: '2rem' }}>
                <label className="form-label">Verification Method</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {[
                    { value: 'dns', label: 'DNS Verification', desc: 'Add a TXT record to your DNS settings' },
                    { value: 'html', label: 'HTML File Verification', desc: 'Upload a verification file to your website' },
                  ].map((opt) => (
                    <label key={opt.value} style={{
                      padding: '1rem', border: verificationMethod === opt.value ? '2px solid var(--gold)' : '1px solid var(--gray)',
                      borderRadius: '0.5rem', cursor: 'pointer',
                      background: verificationMethod === opt.value ? 'rgba(245, 158, 11, 0.05)' : 'var(--white)',
                    }}>
                      <input type="radio" value={opt.value} checked={verificationMethod === opt.value}
                        onChange={(e) => { setVerificationMethod(e.target.value); setError(''); }} style={{ marginRight: '0.75rem' }} />
                      <strong>{opt.label}</strong>
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginTop: '0.25rem', marginLeft: '1.5rem' }}>{opt.desc}</p>
                    </label>
                  ))}
                </div>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Adding domain...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Step 3: Verify Domain ── */}
      {step === 3 && (
        <div>
          <h1 className="hero-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Verify Domain Ownership</h1>
          <p style={{ color: 'var(--gray)', marginBottom: '2rem' }}>Complete the verification to start monitoring</p>

          <div className="card-emphasis">
            {verificationMethod === 'dns' ? (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                  <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Add this TXT record to your DNS:</p>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    <p><strong>Type:</strong> TXT</p>
                    <p><strong>Name:</strong> _kagi-verification</p>
                    <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <strong>Value:</strong>
                      <code style={{ background: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', flex: 1 }}>{verificationToken}</code>
                      <button onClick={() => copyToClipboard(verificationToken)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }} title="Copy">
                        {copied ? <Check size={16} color="var(--success)" /> : <Copy size={16} />}
                      </button>
                    </p>
                  </div>
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>
                  DNS changes may take up to 48 hours to propagate. Click "Verify Domain" once you've added the TXT record.
                </p>
              </div>
            ) : (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                  <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Upload this file to your website:</p>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    <p><strong>Filename:</strong> kagi-verification.txt</p>
                    <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <strong>Content:</strong>
                      <code style={{ background: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', flex: 1 }}>{verificationToken}</code>
                      <button onClick={() => copyToClipboard(verificationToken)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }} title="Copy">
                        {copied ? <Check size={16} color="var(--success)" /> : <Copy size={16} />}
                      </button>
                    </p>
                    <p><strong>Location:</strong> https://{domain}/kagi-verification.txt</p>
                  </div>
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>
                  The file must be accessible at the root of your domain over HTTPS. Click "Verify Domain" once uploaded.
                </p>
              </div>
            )}

            {error && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>{error}</div>
            )}

            {verified ? (
              <div style={{ background: '#d1fae5', color: '#065f46', padding: '1rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <CheckCircle size={24} />
                <span style={{ fontWeight: 600 }}>Domain verified successfully!</span>
              </div>
            ) : (
              <button onClick={handleVerify} disabled={loading} className="btn btn-primary" style={{ width: '100%' }}>
                {loading ? 'Verifying...' : 'Verify Domain'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Step 4: Complete ── */}
      {step === 4 && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{ width: '80px', height: '80px', background: '#d1fae5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem' }}>
              <CheckCircle size={48} color="#065f46" />
            </div>
            <h1 className="hero-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Setup Complete!</h1>
            <p style={{ color: 'var(--gray)', fontSize: '1.1rem' }}>Your trust monitoring is now active for {domain}</p>
          </div>
          <div className="card-emphasis" style={{ marginBottom: '2rem' }}>
            <h3 className="subsection-title" style={{ marginBottom: '1rem' }}>What's Next?</h3>
            <ul style={{ paddingLeft: '1.5rem', color: 'var(--charcoal)' }}>
              <li>Initial scan will complete in ~5 minutes</li>
              <li>Trust Score will be calculated and displayed</li>
              <li>You'll receive alerts for any issues detected</li>
              <li>Configure additional alert channels in settings</li>
            </ul>
          </div>
          <button onClick={handleComplete} className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Saving...' : 'Go to Dashboard'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MonitoringOnboarding;
